import { Movie } from "@/entry/server/movie";
import { Pagination } from "@/lib/api";
import React, { useEffect, useState } from "react";
import LoadingGif from "@public/img/loading.gif";
import SectionTopBg from "@public/img/section-top-bg.png";
import CalendarIcon from "@public/img/calendar.svg";
import CircleUserIcon from "@public/img/user-circle.svg";
import PaginationView from "@/component/commom/pagination";
import MovieCard from "@/component/commom/movie-card";
import { userRepo } from "@/repo/user-repo";
import { PublicListData } from "@/entry/server/user";

type ListMoviesGridProps = {
    defaultMovies: Movie[];
    defaultPagination: Pagination;
    id: number;
    list: PublicListData;
}

export default function ListMoviesGrid({
    defaultMovies,
    defaultPagination,
    id,
    list,
}: ListMoviesGridProps) {
    const [movies, setMovies] = useState<Movie[]>(defaultMovies);
    const [pagination, setPagination] = useState<Pagination>(defaultPagination);
    const [isLoading, setIsLoading] = useState(false)
    const [page, setPage] = useState<number>(defaultPagination.current_page);

    useEffect(() => {
        if (isNaN(Number(page))) {
            setPage(defaultPagination.current_page);
        }

        if (page < 1 || page > pagination.total_pages) {
            setPage(defaultPagination.current_page);
        }

        updateQueryParams();
        fetchMovies();
    }, [page]);

    const fetchMovies = async () => {
        setIsLoading(true);
        const response = await userRepo.getListMovies(id, Number(page), 12);
        if (response.status) {
            setMovies(response.data!);
            setPagination(response.pagination!);
        }
        setIsLoading(false);
    }

    const updateQueryParams = () => {
        const params = new URLSearchParams();
        if (page > 1) {
            params.set("page", page.toString());
        } else {
            params.delete("page");
        }

        window.history.pushState({}, "", `/lists/${id}?${params.toString()}`);
    }

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-screen">
                <img src={LoadingGif} alt="Loading" />
                <p className="text-slate-100 text-sm mt-4">در حال بارگذاری...</p>
            </div>
        );
    }

    return (
        <div role="main" className="px-5 lg:px-0 mt-20 mb-10 relative bg-main-bg">
            <img src={SectionTopBg} alt="Section Top Background" className="absolute top-0 bottom-0 left-0 w-full h-auto object-cover" />
            <div className="inset-0 mt-16 flex flex-col items-center gap-8 h-fit bg-main-bg z-10 container mx-auto">
                <div className="w-full py-4 px-8 rounded-3xl backdrop-blur-xs relative flex flex-col xl:flex-row items-start xl:items-center gap-4 xl:gap-0" id="agent-info">
                    <div className="flex flex-col items-center lg:items-start xl:flex-1 w-full xl:w-auto">
                        <div className="flex items-center gap-2">
                            <h1 className="text-3xl font-extrabold text-slate-100">{list.name}</h1>
                            <span className="text-lg text-zinc-100 line-clamp-1">({list.movie_count} آثار)</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <img src={CircleUserIcon} alt="Circle User" />
                            <span className="text-sm text-zinc-400 line-clamp-1 -mb-1">{list.username}</span>

                            <img src={CalendarIcon} alt="Calendar" className="mr-4" />
                            <span className="text-sm text-zinc-400 line-clamp-1 -mb-1">{list.created_at}</span>
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-6 w-full">
                    {movies.map(function (movie) {
                        return (
                        <MovieCard
                            key={movie.id}
                            id={movie.id}
                            slug={movie.slug}
                            type={movie.type}
                            title={movie.title}
                            updateText={movie.update_text}
                            year={movie.year != null ? String(movie.year) : ""}
                            ratingValue={movie.imdb_rate != null ? String(movie.imdb_rate) : "0"}
                            thumbnail={movie.thumbnail}
                            isDubbed={movie.is_dubbed}
                            isSubtitled={movie.is_subbed}
                            genres={movie.genres ? movie.genres : []}
                        />
                        );
                    })}
                </div>

                <PaginationView pagination={pagination} onChange={(page) => setPage(page)} />
            </div>
        </div>
    )
}
